import { useTranslate } from '@refinedev/core';
import { Dropdown, Button } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import { ModStatus } from '@/api/generated/schemas';
import {
  useBulkModStatus,
  type BulkModStatusOutcome,
} from '@/hooks/user_catalog/useBulkModStatus';
import { MOD_STATUS_BULK_LIMIT } from '@/components/user_catalog/modStatusBulk';

interface BulkModStatusControlProps {
  /** Refine resource string, e.g. `user_catalog.tags` */
  resource: string;
  /** Row ids to update (selected rows in List, single record in Show). */
  ids: number[];
  /** Names a refused id in the result modal; `#id` without one. */
  labelOf?: (id: number) => string | null | undefined;
  disabled?: boolean;
  /** The request went through — some ids may still have been refused. */
  onSuccess?: (outcome: BulkModStatusOutcome) => void;
  size?: SizeType;
}

/**
 * Dropdown of `mod_status` values. Picking one confirms then applies to `ids`.
 * Shared by List (bulk over selected rows) and Show (single record) views.
 */
export const BulkModStatusControl = ({
  resource,
  ids,
  labelOf,
  disabled,
  onSuccess,
  size,
}: BulkModStatusControlProps) => {
  const t = useTranslate();
  const { update, isLoading } = useBulkModStatus({ resource, labelOf });

  const items = Object.values(ModStatus).map((value) => ({
    key: value,
    label: t(`base.mod_status.${value}`),
  }));

  return (
    <Dropdown
      trigger={['click']}
      disabled={
        disabled || ids.length === 0 || ids.length > MOD_STATUS_BULK_LIMIT
      }
      menu={{
        items,
        onClick: ({ key }) => update(ids, key as ModStatus, onSuccess),
      }}
    >
      <Button type="primary" size={size} loading={isLoading}>
        {t('user_catalog.common.bulk_mod_status.apply')} <DownOutlined />
      </Button>
    </Dropdown>
  );
};
